import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { openGraphConfig, OpenGraphConfig } from '../config/opengraph';

interface OpenGraphMetaProps {
  config?: Partial<OpenGraphConfig>;
}

/** document.head에 og:image, twitter:image 직접 주입. 기존 동일 태그는 제거 후 하나만 유지 (react-helmet 중복 방지) */
function syncOgImageToHead(imageUrl: string) {
  if (typeof document === 'undefined') return;

  document.querySelectorAll('meta[property="og:image"]').forEach((el) => el.remove());
  const ogImage = document.createElement('meta');
  ogImage.setAttribute('property', 'og:image');
  ogImage.setAttribute('content', imageUrl);
  document.head.appendChild(ogImage);

  document.querySelectorAll('meta[name="twitter:image"]').forEach((el) => el.remove());
  const twImage = document.createElement('meta');
  twImage.setAttribute('name', 'twitter:image');
  twImage.setAttribute('content', imageUrl);
  document.head.appendChild(twImage);
}

/**
 * Open Graph 메타 태그 컴포넌트
 *
 * 사용 방법:
 * 1. 기본 설정 사용: <OpenGraphMeta />
 * 2. 커스텀 설정: <OpenGraphMeta config={{ title: '커스텀 제목', description: '...' }} />
 */
export default function OpenGraphMeta({ config }: OpenGraphMetaProps) {
  const ogConfig = { ...openGraphConfig, ...config };
  const prevImageRef = useRef<string | null>(null);

  useEffect(() => {
    const imageUrl = ogConfig.image ?? '';
    if (imageUrl && imageUrl !== prevImageRef.current) {
      syncOgImageToHead(imageUrl);
      prevImageRef.current = imageUrl;
    }
  }, [ogConfig.image]);

  return (
    <Helmet>
      {/* 기본 메타 태그 */}
      <title>{ogConfig.title}</title>
      <meta name="description" content={ogConfig.description} />
      {ogConfig.keywords && <meta name="keywords" content={ogConfig.keywords.join(', ')} />}
      {ogConfig.author && <meta name="author" content={ogConfig.author} />}
      {ogConfig.themeColor && <meta name="theme-color" content={ogConfig.themeColor} />}

      {/* Open Graph 기본 태그 */}
      <meta property="og:type" content={ogConfig.type} />
      <meta property="og:title" content={ogConfig.title} />
      <meta property="og:description" content={ogConfig.description} />
      <meta property="og:url" content={ogConfig.url} />
      <meta property="og:site_name" content={ogConfig.siteName} />
      <meta property="og:locale" content={ogConfig.locale} />

      {/* Open Graph 이미지 태그 */}
      <meta property="og:image" content={ogConfig.image} />
      <meta property="og:image:alt" content={ogConfig.imageAlt} />
      {ogConfig.imageWidth && <meta property="og:image:width" content={ogConfig.imageWidth} />}
      {ogConfig.imageHeight && <meta property="og:image:height" content={ogConfig.imageHeight} />}

      {/* Twitter Card 태그 */}
      <meta name="twitter:card" content={ogConfig.twitterCard} />
      <meta name="twitter:title" content={ogConfig.title} />
      <meta name="twitter:description" content={ogConfig.description} />
      <meta name="twitter:image" content={ogConfig.image} />
      {ogConfig.twitterSite && <meta name="twitter:site" content={ogConfig.twitterSite} />}
      {ogConfig.twitterCreator && <meta name="twitter:creator" content={ogConfig.twitterCreator} />}
    </Helmet>
  );
}
